import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Table,
  Typography,
  Modal,
  Tooltip,
  Space,
  Descriptions,
  Tag,
  Upload,
  message,
  Image,
  DatePicker,
} from "antd";
import { Severty, ShowToast } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";
import { 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined, 
  ExclamationCircleFilled, 
  SearchOutlined,
  UploadOutlined,
  DeleteFilled,
  CalendarOutlined 
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const serviceTypes = ["Standard", "Premium", "Custom"];
const pricingModels = ["Fixed", "Variable", "Subscription"];
const roles = ["Admin", "Vendor", "Client"];

const AdminServicesPage = () => {
  const { request } = useRequest();
  const [services, setServices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serviceModalVisible, setServiceModalVisible] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [vendorModalVisible, setVendorModalVisible] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [deletingService, setDeletingService] = useState(null);
  const [viewingService, setViewingService] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  
  // Search and filter states
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);

  // Debounce state
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Fetch services with pagination and filters
  const fetchServices = (page = 1, pageSize = 10, filters = {}) => {
    setLoading(true);
    
    let queryParams = `page=${page}&page_size=${pageSize}`;
    
    if (filters.searchKeyword && filters.searchKeyword.trim()) {
      queryParams += `&search=${filters.searchKeyword.trim()}`;
    }
    
    if (filters.status && filters.status !== "all") {
      queryParams += `&status=${filters.status}`;
    }

    if (filters.dateRange && filters.dateRange.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      queryParams += `&start_date=${startDate.format('YYYY-MM-DD')}&end_date=${endDate.format('YYYY-MM-DD')}`;
    }

    request({
      url: `/service/service_list?${queryParams}`,
      method: "GET",
      onSuccess: ({ data, pagination: paginationData }) => {
        setServices(data || []);
        setPagination({
          current: paginationData?.current_page || 1,
          pageSize: paginationData?.page_size || 10,
          total: paginationData?.total_count || 0,
        });
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
        ShowToast("Failed to fetch services", Severty.ERROR);
      },
    });
  };

  // Handle table pagination changes
  const handleTableChange = (pagination) => {
    fetchServices(pagination.current, pagination.pageSize, {
      searchKeyword,
      status: statusFilter,
      dateRange
    });
  };

  useEffect(() => {
    fetchServices(pagination.current, pagination.pageSize);
  }, []);

  // Handle search with debounce
  const handleSearch = (keyword) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setSearchKeyword(keyword);
      fetchServices(1, pagination.pageSize, {
        searchKeyword: keyword,
        status: statusFilter,
        dateRange
      });
    }, 500);

    setSearchTimeout(timeout);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    handleSearch(value);
  };

  // Handle form submission (if user still presses enter)
  const handleSearchSubmit = (values) => {
    const { keyword, date_range } = values;
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchKeyword(keyword);
    setDateRange(date_range);
    fetchServices(1, pagination.pageSize, {
      searchKeyword: keyword,
      status: statusFilter,
      dateRange: date_range
    });
  };

  // Handle status filter change - convert to 1 for Active, 0 for Inactive
  const handleStatusFilterChange = (value) => {
    let statusValue;
    
    if (value === "active") {
      statusValue = "1";
    } else if (value === "inactive") {
      statusValue = "0";
    } else {
      statusValue = "all";
    }
    
    setStatusFilter(statusValue);
    fetchServices(1, pagination.pageSize, {
      searchKeyword,
      status: statusValue,
      dateRange
    });
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    fetchServices(1, pagination.pageSize, {
      searchKeyword,
      status: statusFilter,
      dateRange: dates
    });
  };

  // Reset filters
  const resetFilters = () => {
    setSearchKeyword("");
    setStatusFilter("all");
    setDateRange(null);
    searchForm.resetFields();
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    fetchServices(1, pagination.pageSize);
  };

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Get relative time (e.g., "2 days ago")
  const getRelativeTime = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = now - date;
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
      
      if (diffInDays === 0) {
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        if (diffInHours === 0) {
          const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
          return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
        }
        return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
      } else if (diffInDays === 1) {
        return "Yesterday";
      } else if (diffInDays < 7) {
        return `${diffInDays} days ago`;
      } else if (diffInDays < 30) {
        const weeks = Math.floor(diffInDays / 7);
        return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
      } else {
        const months = Math.floor(diffInDays / 30);
        return `${months} month${months !== 1 ? 's' : ''} ago`;
      }
    } catch (error) {
      return "N/A";
    }
  };

  const openServiceModal = (service = null) => {
    setEditingService(service);
    setImageFile(null);
    setImagePreview(null);
    setUploadedImageUrl(null);
    form.resetFields();
    if (service) {
      form.setFieldsValue({
        ...service,
        pricingModel: service.price,
        status: service.status === 1,
      });
      if (service.image) {
        setImagePreview(service.image);
        setUploadedImageUrl(service.image);
      }
    } else {
      form.setFieldsValue({ status: true });
    }
    setServiceModalVisible(true);
  };

  // UNIVERSAL SOLUTION - Handles multiple response structures
  const handleImageUpload = async (file) => {
    return new Promise((resolve, reject) => {
      setUploadLoading(true);
      
      const formData = new FormData();
      formData.append('image', file);
      
      request({
        url: '/auth/image_upload',
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onSuccess: (response) => {
          console.log('Upload response received:', response);
          
          // Extract image URL from multiple possible response structures
          const findImageUrl = (data) => {
            // Check direct properties
            if (data?.image_url) return data.image_url;
            if (data?.url) return data.url;
            if (data?.fileUrl) return data.fileUrl;
            
            // Check nested structures
            if (data?.data?.image_url) return data.data.image_url;
            if (data?.data?.url) return data.data.url;
            
            // Check files array
            if (Array.isArray(data?.files) && data.files[0]?.image_url) {
              return data.files[0].image_url;
            }
            if (Array.isArray(data?.files) && data.files[0]?.url) {
              return data.files[0].url;
            }
            
            // Check response directly
            if (data?.status === 200 && data?.data?.image_url) {
              return data.data.image_url;
            }
            
            return null;
          };
          
          const imageUrl = findImageUrl(response.data) || findImageUrl(response);
          
          if (imageUrl) {
            console.log('Image upload successful:', imageUrl);
            
            setUploadedImageUrl(imageUrl);
            setImageFile(file);
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
            
            message.success('Image uploaded successfully');
            setUploadLoading(false);
            resolve(false);
          } else {
            console.error('Could not find image URL in response:', response);
            const errorMessage = response.data?.message || 'Upload successful but no image URL received';
            setUploadLoading(false);
            reject(new Error(errorMessage));
          }
        },
        onError: (error) => {
          console.error('Upload request failed:', error);
          const errorMessage = error.response?.data?.message || 
                              error.response?.data?.error || 
                              error.message || 
                              'Upload failed';
          setUploadLoading(false);
          reject(new Error(errorMessage));
        },
      });
    });
  };

  // Upload props
  const uploadProps = {
    beforeUpload: async (file) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
      if (!isJpgOrPng) {
        message.error('You can only upload JPG/PNG files!');
        return false;
      }
      
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('Image must be smaller than 5MB!');
        return false;
      }
      
      try {
        await handleImageUpload(file);
        return false;
      } catch (error) {
        message.error(`Image upload failed: ${error.message}`);
        console.error('Upload error:', error);
        return false;
      }
    },
    showUploadList: false,
    accept: '.jpg,.jpeg,.png',
    disabled: uploadLoading,
  };

  // Remove image
  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setUploadedImageUrl(null);
    form.setFieldsValue({ image: null });
  };

  // Open view service modal
  const openViewModal = (service) => {
    setViewLoading(true);
    setViewingService(null);
    setViewModalVisible(true);
  
    request({
      url: `/service/get_service?id=${service.id}`,
      method: "GET",
      onSuccess: (response) => {
        setViewingService(response?.data || service);
        setViewLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.error || "Failed to fetch service details", Severty.ERROR);
        setViewLoading(false);
        setViewingService(service);
      },
    });
  };

  // Close view modal
  const closeViewModal = () => {
    setViewModalVisible(false);
    setViewingService(null);
    setViewLoading(false);
  };

  // Service submission with proper image URL handling
  const onServiceSubmit = (values) => {
    setLoading(true);
    
    const payload = {
      name: values.name,
      description: values.description,
      sla: values.sla,
      price: values.pricingModel,
      // Explicitly set status for both create and update
      status: values.status ? 1 : 0,
    };

    // For new services, ensure status is true (1) by default
    if (!editingService) {
      payload.status = 1; // Default to active for new services
    }

    if (uploadedImageUrl) {
      payload.image = uploadedImageUrl;
      console.log('Including image URL in payload:', uploadedImageUrl);
    }

    if (editingService) {
      payload.id = editingService.id;
      // For editing, use the form value
      payload.status = values.status ? 1 : 0;
    }

    console.log('Final payload being sent:', payload);

    request({
      url: editingService ? `/service/update_service` : "/service/service_create",
      method: editingService ? "PUT" : "POST",
      data: payload,
      onSuccess: () => {
        ShowToast(`Service ${editingService ? 'updated' : 'added'} successfully`, Severty.SUCCESS);
        setServiceModalVisible(false);
        fetchServices(pagination.current, pagination.pageSize, {
          searchKeyword,
          status: statusFilter,
          dateRange
        });
        setLoading(false);
      },
      onError: (err) => {
        console.error('Service creation error:', err);
        ShowToast(err?.response?.data?.message || `Failed to ${editingService ? 'update' : 'create'} service`, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  const toggleServiceStatus = (record) => {
    const newStatus = record.status === 1 ? 0 : 1;
    request({
      url: `/service/update_service_status`,
      method: "PUT",
      data: {
        id: record.id,
        status: newStatus,
      },
      onSuccess: () => {
        ShowToast("Service status updated successfully", Severty.SUCCESS);
        fetchServices(pagination.current, pagination.pageSize, {
          searchKeyword,
          status: statusFilter,
          dateRange
        });
      },
      onError: (err) => ShowToast(err?.response?.data?.message, Severty.ERROR),
    });
  };

  // Open delete confirmation modal
  const openDeleteModal = (service) => {
    setDeletingService(service);
    setDeleteModalVisible(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setDeleteModalVisible(false);
    setDeletingService(null);
    setDeleteLoading(false);
  };

  // Delete service function
  const handleDeleteService = () => {
    if (!deletingService) return;

    setDeleteLoading(true);
    request({
      url: `/service/delete_service`,
      method: "DELETE",
      data: { id: deletingService.id },
      onSuccess: () => {
        ShowToast("Service deleted successfully", Severty.SUCCESS);
        fetchServices(pagination.current, pagination.pageSize, {
          searchKeyword,
          status: statusFilter,
          dateRange
        });
        closeDeleteModal();
      },
      onError: (err) => {
        const errorMessage = err?.response?.data?.error || "Failed to delete service";
        ShowToast(errorMessage, Severty.ERROR);
        setDeleteLoading(false);
      },
    });
  };

  // Function to trim text to a specified length
  const trimText = (text, maxLength = 50) => {
    if (!text) return "N/A";
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const serviceColumns = [
    { 
      title: "Image", 
      dataIndex: "image_url", 
      key: "image_url",
      width: 80,
      render: (image) => (
        image ? (
          <Image
            width={50}
            height={50}
            src={image}
            alt="Service"
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
          />
        ) : (
          <div style={{
            width: 50,
            height: 50,
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4,
            color: '#999'
          }}>
            No Image
          </div>
        )
      )
    },
    { title: "Service Name", dataIndex: "name", key: "name" },
    { 
      title: "Description", 
      dataIndex: "description", 
      key: "description",
      render: (text) => trimText(text, 30)
    },
    { 
      title: "SLA", 
      dataIndex: "sla", 
      key: "sla",
      render: (text) => trimText(text, 30)
    },
    { title: "Pricing Model", dataIndex: "price", key: "price" },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (createdAt, record) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <CalendarOutlined style={{ marginRight: '4px' }} />
            {formatDate(createdAt)}
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>
            {getRelativeTime(createdAt)}
          </div>
        </div>
      ),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Switch
          checked={status === 1}
          onChange={() => toggleServiceStatus(record)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Service">
            <Button 
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openViewModal(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Service">
            <Button 
              size="small"
              icon={<EditOutlined />}
              onClick={() => openServiceModal(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Service">
            <Button 
              size="small"
              danger 
              icon={<DeleteOutlined />}
              onClick={() => openDeleteModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-content">
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>Define Services</Title>
          </Col>
          <Col>
            <Button type="primary" onClick={() => openServiceModal()}>
              Add New Service
            </Button>
          </Col>
        </Row>

        {/* Search and Filter Section */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form
            form={searchForm}
            layout="vertical"
            onFinish={handleSearchSubmit}
            initialValues={{
              keyword: "",
              status: "all",
              date_range: null
            }}
          >
            <Row gutter={[16, 16]} align="bottom">
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item
                  label="Search Services"
                  name="keyword"
                >
                  <Input 
                    placeholder="Search by service name or description..." 
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={handleSearchChange}
                    onPressEnter={(e) => {
                      e.preventDefault();
                      handleSearchSubmit(searchForm.getFieldsValue());
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item
                  label="Status"
                  name="status"
                >
                  <Select onChange={handleStatusFilterChange}>
                    <Option value="all">All Status</Option>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item
                  label="Date Range"
                  name="date_range"
                >
                  <RangePicker 
                    style={{ width: '100%' }}
                    onChange={handleDateRangeChange}
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label=" " colon={false}>
                  <Space>
                    <Button 
                      type="primary" 
                      htmlType="submit"
                      icon={<SearchOutlined />}
                    >
                      Search
                    </Button>
                    {/* <Button onClick={resetFilters}>
                      Reset
                    </Button> */}
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {/* Active Filters Display */}
          {(searchKeyword || statusFilter !== "all" || dateRange) && (
            <div style={{ marginTop: 16, padding: 8, background: '#f0f8ff', borderRadius: 6 }}>
              <Space wrap>
                <strong>Active Filters:</strong>
                {searchKeyword && (
                  <Tag color="blue">Search: "{searchKeyword}"</Tag>
                )}
                {statusFilter !== "all" && (
                  <Tag color="blue">Status: {statusFilter === "1" ? "Active" : "Inactive"}</Tag>
                )}
                {dateRange && (
                  <Tag color="blue">
                    Date: {dateRange[0].format('YYYY-MM-DD')} to {dateRange[1].format('YYYY-MM-DD')}
                  </Tag>
                )}
                <Button type="link" size="small" onClick={resetFilters}>
                  Clear All
                </Button>
              </Space>
            </div>
          )}
        </Card>

        <Table
          rowKey="id"
          columns={serviceColumns}
          dataSource={services}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          className="mt-3"
        />
      </Card>

      {/* Add/Edit Service Modal */}
      <Modal
        open={serviceModalVisible}
        footer={null}
        className="tab_modal"
        onCancel={() => setServiceModalVisible(false)}
        width={600}
      >
        <div className="modal_header">
          {editingService ? "Edit Service" : "Add New Service"}
        </div>
        <Form form={form} layout="vertical" onFinish={onServiceSubmit}>
          {/* Image Upload Section */}
          <Form.Item
            label="Service Image"
            name="image"
          >
            <div>
              {imagePreview && (
                <div style={{ marginBottom: 16, position: 'relative', display: 'inline-block' }}>
                  <Image
                    width={150}
                    height={150}
                    src={imagePreview}
                    alt="Service preview"
                    style={{ objectFit: 'cover', borderRadius: 8 }}
                  />
                  <Button
                    type="primary"
                    danger
                    size="small"
                    icon={<DeleteFilled />}
                    style={{ position: 'absolute', top: 8, right: 8 }}
                    onClick={handleRemoveImage}
                  />
                </div>
              )}
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />} loading={uploadLoading}>
                  {imagePreview ? 'Change Image' : 'Upload Image'}
                </Button>
              </Upload>
              <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                Supports JPG, PNG. Max 5MB.
              </div>
              {uploadedImageUrl && (
                <div style={{ fontSize: 12, color: '#52c41a', marginTop: 4 }}>
                  ✓ Image ready to be saved: {uploadedImageUrl.substring(0, 50)}...
                </div>
              )}
            </div>
          </Form.Item>

          <Form.Item
            label="Service Name"
            name="name"
            rules={[{ required: true, message: "Enter service name" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Enter description" }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            label="SLA"
            name="sla"
            rules={[{ required: true, message: "Enter SLA" }]}
          >
            <Input placeholder="Response time / Resolution time" />
          </Form.Item>

          <Form.Item
            label="Pricing Model"
            name="pricingModel"
            rules={[{ required: true, message: "Select pricing model" }]}
          >
            <Select>
              {pricingModels.map((p) => (
                <Option key={p} value={p}>
                  {p}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Only show status field when editing, not when creating new service */}
          {editingService && (
            <Form.Item label="Status" name="status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          )}

          <Form.Item>
            <div className="modal_footer">
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingService ? "Update Service" : "Add Service"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Service Modal */}
      <Modal
        open={viewModalVisible}
        onCancel={closeViewModal}
        footer={[
          <Button key="close" onClick={closeViewModal}>
            Close
          </Button>,
        ]}
        width={600}
        className="tab_modal"
      >
        <div className="modal_header">
          Service Details
        </div>
        {viewingService && (
          <div style={{ padding: '20px 0' }}>
            {/* Service Image in View Modal */}
            {viewingService.image && (
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <Image
                  width={200}
                  height={200}
                  src={viewingService.image}
                  alt="Service"
                  style={{ objectFit: 'cover', borderRadius: 8 }}
                />
              </div>
            )}
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Service Name">
                <strong>{viewingService.name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                {viewingService.description || "Not provided"}
              </Descriptions.Item>
              <Descriptions.Item label="SLA">
                {viewingService.sla || "Not provided"}
              </Descriptions.Item>
              <Descriptions.Item label="Pricing Model">
                {viewingService.price || "Not provided"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={viewingService.status === 1 ? "green" : "red"}>
                  {viewingService.status === 1 ? "Active" : "Inactive"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {formatDate(viewingService.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Account Age">
                {getRelativeTime(viewingService.created_at)}
              </Descriptions.Item>
              {viewingService.updated_at && (
                <Descriptions.Item label="Last Updated">
                  {new Date(viewingService.updated_at).toLocaleString()}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        )}
        {!viewingService && viewLoading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Loading service details...
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalVisible}
        onCancel={closeDeleteModal}
        footer={[
          <Button key="cancel" onClick={closeDeleteModal}>
            Cancel
          </Button>,
          <Button 
            key="delete" 
            type="primary" 
            danger 
            loading={deleteLoading}
            onClick={handleDeleteService}
          >
            Delete Service
          </Button>,
        ]}
        className="tab_modal"
      >
        <div className="modal_header">
          <ExclamationCircleFilled style={{ color: '#faad14', marginRight: 8 }} />
          Delete Service
        </div>
        <div style={{ padding: '20px 0' }}>
          <p>Are you sure you want to delete this service?</p>
          {deletingService && (
            <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px', marginTop: '12px' }}>
              <strong>Service Details:</strong>
              <div>Name: {deletingService.name}</div>
              <div>Description: {deletingService.description}</div>
              <div>Pricing Model: {deletingService.price}</div>
              <div>Created: {formatDate(deletingService.created_at)}</div>
            </div>
          )}
          <p style={{ color: '#ff4d4f', marginTop: '12px' }}>
            <strong>Warning:</strong> This action cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default AdminServicesPage;